import AxiosInstance from '@/api/axios';
import { PaginationInterface } from '@/components/Base/Pagination';
import { updateProfilePic } from '@/utils/helper';
import { ClientFilterType } from '@/utils/types';
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

export const fetchClients = createAsyncThunk(
  'fetchClients/fetchData',
  async ({ currentPage, perpage, search }: ClientFilterType, thunkAPI) => {
    try {
      const response = await AxiosInstance.get(`/clients`, {
        params: {
          page: currentPage,
          perpage: perpage,
          search: search,
        },
      });
      if (response.data.success) {
        return response.data.data;
      } else {
        return null;
      }
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    }
  }
);

export interface IClient {
  _id: string;
  address: string;
  archive: boolean;
  salesReport: boolean;
  email: string;
  name: string;
  status: string;
  timezone: string;
  country: string;
  createdAt: string;
  updatedAt: string;
  reEngage: boolean;
  media?: {
    _id: string;
    path?: string;
  };
  role?: string;
}

interface ClientsStateType {
  clients: {
    data: IClient[] | null | undefined;
    pagination: PaginationInterface;
    selectedClientProfile: string | null;
    tempSelectedClient: IClient | null;
  };
  loading: boolean;
  error: string | null;
}

const initialState: ClientsStateType = {
  clients: {
    data: null,
    pagination: {
      currentPage: 1,
      perpage: 25,
      totalPages: 1,
      total: null,
    },
    selectedClientProfile: null,
    tempSelectedClient: null,
  },
  loading: false,
  error: null,
};

const clientsReducer = createSlice({
  name: 'clients',
  initialState,
  reducers: {
    updatePagination: (state, action) => {
      state.clients.pagination = {
        ...state.clients.pagination,
        ...action.payload,
      };
    },
    unshiftClients: (state, action) => {
      if (!Array.isArray(state.clients.data)) {
        state.clients.data = [];
      }

      state.clients.data = [action.payload, ...state.clients.data];

      state.clients.pagination.total =
        (state.clients.pagination.total || 0) + 1;
    },
    removeClient: (state, action) => {
      if (Array.isArray(state.clients.data)) {
        state.clients.data = state.clients.data.filter(
          (client) => client._id !== action.payload
        );
        state.clients.pagination.total =
          (state.clients.pagination.total || 1) - 1;
      }
    },
    updateClientProfile: (state, action) => {
      state.clients.selectedClientProfile = action.payload;
      updateProfilePic(action.payload);
    },
    updateTempClient: (state, action) => {
      state.clients.tempSelectedClient = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchClients.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchClients.fulfilled, (state, action) => {
        state.loading = false;
        state.clients = action.payload;
      })
      .addCase(fetchClients.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export const {
  updatePagination,
  unshiftClients,
  removeClient,
  updateClientProfile,
  updateTempClient,
} = clientsReducer.actions;

export default clientsReducer.reducer;
